import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Snackbar,
} from '@mui/material';
import { Add, Edit, Delete, Settings as SettingsIcon } from '@mui/icons-material';
import {
  GET_EVALUATION_TYPES,
  GET_EVALUATION_API_KEYS,
  CREATE_EVALUATION_TYPE,
  UPDATE_EVALUATION_TYPE,
  DELETE_EVALUATION_TYPE,
  GET_CURRENT_USER_WITH_ROLE,
  EvaluationTypeItem,
  EvaluationAPIKeyItem,
  CustomEvalTestDefinition,
} from '../graphql/settings';
import CustomEvaluationTestBuilder, {
  ensureCustomTestIds,
  sanitizeCustomTestsForPersistence,
} from '../components/CustomEvaluationTestBuilder';

const EvaluationTypes: React.FC = () => {
  const { data: meData } = useQuery(GET_CURRENT_USER_WITH_ROLE);
  const isAdmin = meData?.me?.isAdmin;

  const [filterApiKeyId, setFilterApiKeyId] = useState<string>('');
  const { data: keysData } = useQuery(GET_EVALUATION_API_KEYS, { skip: !isAdmin });
  const { data: typesData, loading, refetch } = useQuery(GET_EVALUATION_TYPES, {
    skip: !isAdmin,
    variables: { evaluationApiKeyId: filterApiKeyId || null },
  });

  const [createMutation] = useMutation(CREATE_EVALUATION_TYPE);
  const [updateMutation] = useMutation(UPDATE_EVALUATION_TYPE);
  const [deleteMutation] = useMutation(DELETE_EVALUATION_TYPE);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EvaluationTypeItem | null>(null);
  const [formApiKeyId, setFormApiKeyId] = useState('');
  const [formEvalKey, setFormEvalKey] = useState('');
  const [formLabel, setFormLabel] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formTests, setFormTests] = useState<CustomEvalTestDefinition[]>([]);
  const [deleteTarget, setDeleteTarget] = useState<EvaluationTypeItem | null>(null);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({
    open: false,
    msg: '',
    sev: 'success',
  });

  const keys: EvaluationAPIKeyItem[] = keysData?.evaluationApiKeys || [];
  const types: EvaluationTypeItem[] = typesData?.evaluationTypes || [];

  const openCreate = () => {
    setEditing(null);
    setFormApiKeyId(keys.filter((k) => k.isActive)[0]?.id || '');
    setFormEvalKey('');
    setFormLabel('');
    setFormDescription('');
    setFormTests([]);
    setDialogOpen(true);
  };

  const openEdit = (row: EvaluationTypeItem) => {
    setEditing(row);
    setFormApiKeyId(row.evaluationApiKeyId);
    setFormEvalKey(row.evaluationKey);
    setFormLabel(row.label);
    setFormDescription(row.description || '');
    setFormTests(ensureCustomTestIds(sanitizeCustomTestsForPersistence(row.customTests || [])));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const testsPayload = sanitizeCustomTestsForPersistence(ensureCustomTestIds(formTests));
      if (editing) {
        await updateMutation({
          variables: {
            input: {
              id: editing.id,
              label: formLabel.trim(),
              description: formDescription,
              customTests: testsPayload,
            },
          },
        });
        setSnack({ open: true, msg: 'Evaluation type updated.', sev: 'success' });
      } else {
        if (!formEvalKey.trim()) {
          setSnack({ open: true, msg: 'Evaluation key is required.', sev: 'error' });
          return;
        }
        if (!formApiKeyId) {
          setSnack({ open: true, msg: 'Select an evaluation API key.', sev: 'error' });
          return;
        }
        await createMutation({
          variables: {
            input: {
              evaluationApiKeyId: formApiKeyId,
              evaluationKey: formEvalKey.trim(),
              label: formLabel.trim() || formEvalKey.trim(),
              description: formDescription,
              customTests: testsPayload,
            },
          },
        });
        setSnack({ open: true, msg: 'Evaluation type created.', sev: 'success' });
      }
      setDialogOpen(false);
      refetch();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setSnack({ open: true, msg, sev: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation({ variables: { id: deleteTarget.id } });
      setSnack({ open: true, msg: 'Evaluation type deleted.', sev: 'success' });
      setDeleteTarget(null);
      refetch();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Delete failed';
      setSnack({ open: true, msg, sev: 'error' });
    }
  };

  if (!meData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Only company admins can manage evaluation types.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: '#f8faf9', minHeight: '100vh' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            Evaluation types
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
            Each type corresponds to one <code>evaluation_key</code> in evaluation API POST bodies for a chosen API key.
            Set a display label, optional description, and default custom tests. Stats reflect sampled runs matching that
            key.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button component={RouterLink} to="/settings" startIcon={<SettingsIcon />} variant="outlined">
            Evaluation API keys
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ bgcolor: '#1e3a5f' }}>
            New type
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2">Filter by API key:</Typography>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="filter-key-label">API key</InputLabel>
            <Select
              labelId="filter-key-label"
              label="API key"
              value={filterApiKeyId}
              onChange={(e) => setFilterApiKeyId(e.target.value)}
            >
              <MenuItem value="">All keys</MenuItem>
              {keys.map((k) => (
                <MenuItem key={k.id} value={k.id}>
                  {k.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
              <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Evaluation key</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>API key</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Runs
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Completed
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Failed
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Pending
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Tests pass %
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last run</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : types.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No evaluation types yet. Create one or adjust the filter.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              types.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.label}</TableCell>
                  <TableCell>
                    <Chip size="small" label={row.evaluationKey} variant="outlined" />
                  </TableCell>
                  <TableCell>{row.evaluationApiKeyName}</TableCell>
                  <TableCell align="right">{row.totalRuns}</TableCell>
                  <TableCell align="right">{row.completedRuns}</TableCell>
                  <TableCell align="right">{row.failedRuns}</TableCell>
                  <TableCell align="right">{row.pendingRuns}</TableCell>
                  <TableCell align="right">
                    {row.customTestsPassRate == null ? '—' : `${row.customTestsPassRate}%`}
                  </TableCell>
                  <TableCell>{row.lastRunAt ? new Date(row.lastRunAt).toLocaleString() : '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(row)} aria-label="Edit">
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)} aria-label="Delete">
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit evaluation type' : 'New evaluation type'}</DialogTitle>
        <DialogContent dividers>
          {!editing && (
            <>
              <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                <InputLabel>Evaluation API key</InputLabel>
                <Select
                  value={formApiKeyId}
                  label="Evaluation API key"
                  onChange={(e) => setFormApiKeyId(e.target.value)}
                >
                  {keys
                    .filter((k) => k.isActive)
                    .map((k) => (
                      <MenuItem key={k.id} value={k.id}>
                        {k.name} ({k.keyMasked})
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Evaluation key"
                value={formEvalKey}
                onChange={(e) => setFormEvalKey(e.target.value)}
                helperText="Must match the evaluation_key field in your POST JSON."
                sx={{ mb: 2 }}
                required
              />
            </>
          )}
          {editing && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Evaluation key
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip size="small" label={editing.evaluationKey} sx={{ mr: 1 }} />
                <Chip size="small" label={editing.evaluationApiKeyName} variant="outlined" />
              </Box>
            </Box>
          )}
          <TextField
            fullWidth
            label="Label"
            value={formLabel}
            onChange={(e) => setFormLabel(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />
          <CustomEvaluationTestBuilder
            variant="evaluation_type"
            generateFromPromptContext="api_evaluation"
            tests={formTests}
            onChange={setFormTests}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} sx={{ bgcolor: '#1e3a5f' }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete evaluation type?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove “{deleteTarget?.label}” ({deleteTarget?.evaluationKey})? Past evaluation results remain in API
            Evaluations.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EvaluationTypes;
